import { createSelector } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import type { RootState } from 'app/store';
import { formatDate } from 'utils';
import { PRIVACY_GLOBAL_PLAN_CODE } from 'utils/constants';
import { BRAND_CODES } from 'brand-config';

const selectOfferState = (state: RootState) => state.offer;

export const selectOfferCode = createSelector(
  [selectOfferState],
  (offerState) => offerState.code,
);

export const selectOfferPlanCode = createSelector(
  [selectOfferState],
  (offerState) => offerState.planCode,
);

export const selectCurrentPlanFamily = createSelector(
  [selectOfferState],
  (offerState) => Boolean(offerState.planFamily),
);

export const selectOfferPlanTier = createSelector(
  [selectOfferState],
  (offerState) =>
    offerState.planTier?.trim() ? offerState.planTier.trim() : '',
);

export const selectOfferPlanFeatures = createSelector(
  [selectOfferState],
  (offerState) =>
    Array.isArray(offerState.planFeatures) ? offerState.planFeatures : [],
);

export const selectOfferCustomerBilled = createSelector(
  [selectOfferState],
  (offerState) => offerState.customerBilled,
);

export const selectOfferBillingType = createSelector(
  [selectOfferState],
  (offerState) => offerState.billingType,
);

export const selectOfferPlanCardPriceCents = createSelector(
  [selectOfferState],
  (offerState) => {
    const cp = offerState.customerPrice;
    if (cp != null && !Number.isNaN(Number(cp))) return cp;
    return offerState.price ?? 0;
  },
);

export const selectOfferCustomerPrice = createSelector(
  [selectOfferState],
  (offerState) => offerState.customerPrice,
);

export const selectOfferSubscriptionTermName = createSelector(
  [selectOfferState],
  (offerState) => {
    const tn = offerState.subscriptionPlan?.termName?.trim();
    return tn ? tn.toUpperCase() : 'MONTHLY';
  },
);

export const selectOfferPlanName = createSelector(
  [selectOfferState],
  (offerState) => offerState.planName ?? '',
);

export const selectOfferBrandCode = createSelector(
  [selectOfferState],
  (offerState) => offerState.brandCode,
);

/* Figma renders the membership expiry as "Month DD, YYYY" (e.g. August 10, 2028). */
const TERM_END_DATE_FORMAT = 'MMMM DD, YYYY';

export const selectOfferTermEndDate = createSelector(
  [selectOfferState],
  (offerState) => {
    if (offerState.serviceEndDate) {
      return formatDate(offerState.serviceEndDate, TERM_END_DATE_FORMAT);
    }
    if (offerState.servicePeriod) {
      return dayjs()
        .add(offerState.servicePeriod, 'M')
        .format(TERM_END_DATE_FORMAT);
    }
    return 'N/A';
  },
);

/**
 * Service-end date shaped for the invitor API `serviceEndDate` payload
 * (`YYYY-MM-DD`). Mirrors Angular's `family-plan-child.component.ts:47-52`:
 * prefer the offer's explicit `serviceEndDate` when present, otherwise
 * compute `today + servicePeriod months`. Returns `null` when the offer
 * has neither field — the BE then falls back to its default behaviour.
 *
 * The Add Child screen sends this alongside `useCode` on Minor Defense
 * to trigger the "Activate your Secure Start" email — without it the
 * child record is created but the email never fires.
 */
export const selectOfferServiceEndDateIso = createSelector(
  [selectOfferState],
  (offerState): string | null => {
    if (offerState.serviceEndDate) {
      return dayjs(offerState.serviceEndDate).format('YYYY-MM-DD');
    }
    if (offerState.servicePeriod) {
      return dayjs().add(offerState.servicePeriod, 'M').format('YYYY-MM-DD');
    }
    return null;
  },
);

export const selectOfferPrice = createSelector(
  [selectOfferState],
  (offerState) => offerState.price,
);

export const selectOfferTerm = createSelector(
  [selectOfferState],
  (offerState) => {
    const termDescription =
      offerState.subscriptionPlan?.termDescription?.trim() ?? '';
    if (termDescription) {
      return termDescription;
    }
    const termName = offerState.subscriptionPlan?.termName?.toUpperCase();
    if (termName === 'ANNUAL') {
      return 'Year';
    }
    return 'Month';
  },
);

export const selectIsPrivacyGlobal = createSelector(
  [selectOfferBrandCode, selectOfferPlanCode],
  (brandCode, planCode) =>
    brandCode === BRAND_CODES.privacyShield &&
    planCode === PRIVACY_GLOBAL_PLAN_CODE,
);

export const selectOfferGroupCode = createSelector(
  [selectOfferState],
  (offerState) => offerState.companyCode,
);

/** Employer or group name — only the `eb *` billing types quote it in their summary copy. */
export const selectOfferGroupName = createSelector(
  [selectOfferState],
  (offerState) => offerState.group.name,
);

export const selectIsIDGuardOfferGroup = createSelector(
  [selectOfferState],
  (offerState) => {
    return (
      offerState.group.code === 'itac' ||
      offerState.group.code === 'cuna' ||
      offerState.group.code === 'itacbatlantic' ||
      offerState.group.code === 'itacrbc' ||
      offerState.group.code === 'itaccuna' ||
      offerState.group.code === 'itacsandia' ||
      offerState.group.code === 'itacsynovus'
    );
  },
);

/**
 * The trial terms the retail plan-summary copy quotes. Grouped into one selector because no
 * branch reads any of them in isolation — a trial is described by its kind, its length and its
 * price together (enrollment-plan-summary.component.html:33-47).
 */
export const selectOfferTrialTerms = createSelector(
  [selectOfferState],
  (offerState) => ({
    hasFreeTrial: offerState.hasFreeTrial,
    hasPaidTrial: offerState.hasPaidTrial,
    trialDuration: offerState.trialDuration,
    trialPriceCents: offerState.trialPrice,
  }),
);

export const selectCompanyType = createSelector(
  [selectOfferState],
  (offerState) => offerState.group.type.toLowerCase(),
);

export const selectIsOfferDetailsExist = createSelector(
  [selectOfferState],
  (offerState) => !!offerState.code,
);

export const selectScheduledOffer = createSelector(
  [selectOfferState],
  (offerState) => offerState.scheduledOffer,
);
